import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { adDisplayCoords, distanceKm } from "@/lib/geo-jitter";
import { useServerFn } from "@tanstack/react-start";
import { Search, LayoutGrid, Map as MapIcon, Navigation, Home, Loader2, X } from "lucide-react";
import { listAds, listAdsForMap, countAds, getMyAds } from "@/lib/ads.functions";
import { listSavedPlaces } from "@/lib/addresses.functions";
import { useAuctionsEnabled } from "@/lib/auction-availability";
import { useAuth } from "@/hooks/useAuth";
import type { LucideIcon } from "lucide-react";
import { AdCard, type AdCardData } from "@/components/AdCard";
import { SaveSearchButton } from "@/components/SaveSearchButton";

import { useTopBids } from "@/hooks/useTopBids";
import { CategoryPicker } from "@/components/CategoryPicker";
import { AiSearchPanel, type AiFilters } from "@/components/AiSearchPanel";
import type { MapAd } from "@/components/AdsClusterMap";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { savedPlacesQuery } from "@/lib/queries";
import { useLocale } from "@/lib/i18n/locale-context";

type SortId = "senaste" | "nara" | "billigast" | "dyrast" | "slutar-snart";
type SaleKindId = "alla" | "fixed" | "auction";
type SellerTypeId = "alla" | "private" | "business";
type SearchParams = {
  kategori?: string;
  q?: string;
  maxPris?: number;
  minPris?: number;
  sort?: SortId;
  salj?: SaleKindId;
  saljare?: SellerTypeId;
};

const SORTS: { id: SortId; label: string }[] = [
  { id: "senaste", label: "Senast inlagda" },
  { id: "nara", label: "Nära mig" },
  { id: "billigast", label: "Lägsta pris" },
  { id: "dyrast", label: "Högsta pris" },
  { id: "slutar-snart", label: "Auktioner: slutar snart" },
];

export const SALE_KINDS: { id: SaleKindId; label: string }[] = [
  { id: "alla", label: "Alla" },
  { id: "fixed", label: "Fast pris" },
  { id: "auction", label: "Auktion" },
];

/** Privat eller företag – företag säljer med ångerrätt och reklamationsrätt. */
const SELLER_TYPES: { id: SellerTypeId; label: string }[] = [
  { id: "alla", label: "Alla säljare" },
  { id: "private", label: "Privat" },
  { id: "business", label: "Företag" },
];

/** Färdiga prisspann – allt ställs in i en enda dropdown. */
const PRICES: { id: string; label: string; min?: number; max?: number }[] = [
  { id: "alla", label: "Alla priser" },
  { id: "0-500", label: "Upp till 500 kr", max: 500 },
  { id: "500-1000", label: "500–1 000 kr", min: 500, max: 1000 },
  { id: "1000-2500", label: "1 000–2 500 kr", min: 1000, max: 2500 },
  { id: "2500-5000", label: "2 500–5 000 kr", min: 2500, max: 5000 },
  { id: "5000-10000", label: "5 000–10 000 kr", min: 5000, max: 10000 },
  { id: "10000-25000", label: "10 000–25 000 kr", min: 10000, max: 25000 },
  { id: "25000-", label: "Över 25 000 kr", min: 25000 },
];

/** Vilket spann som är valt utifrån min- och maxpris i adressfältet. */
function priceId(min?: number, max?: number) {
  if (min === undefined && max === undefined) return "alla";
  const hit = PRICES.find((p) => p.min === min && p.max === max);
  return hit?.id ?? "eget";
}


const num = (v: unknown) => {
  const n = typeof v === "string" ? Number(v) : typeof v === "number" ? v : NaN;
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

const kmLabel = (km: number) =>
  km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(km < 10 ? 1 : 0)} km`;

const AdsClusterMap = lazy(() => import("@/components/AdsClusterMap"));

/** Samma inställningar används av både förhämtningen på servern och sidan. */
function adsQueryOptions(input: {
  cats: string[];
  q?: string;
  sort: SortId;
  saleKind?: SaleKindId;
  sellerType?: SellerTypeId;
  country?: "SE" | "ES";
}) {
  const pageSize = input.sort === "nara" ? 500 : 60;
  const saleKind = input.saleKind && input.saleKind !== "alla" ? input.saleKind : undefined;
  const sellerType =
    input.sellerType && input.sellerType !== "alla" ? input.sellerType : undefined;
  const country = input.country ?? "SE";
  return {
    queryKey: [
      "ads",
      input.cats.join(",") || null,
      input.q ?? null,
      input.sort,
      saleKind ?? null,
      sellerType ?? null,
      country,
    ] as const,
    initialPageParam: 0,
    queryFn: ({ pageParam }: { pageParam: unknown }) =>
      listAds({
        data: {
          categories: input.cats,
          search: input.q,
          limit: pageSize,
          offset: pageParam as number,
          sort: input.sort,
          saleKind,
          sellerType,
          country,
        },
      }),
    getNextPageParam: (lastPage: unknown[], allPages: unknown[][]) =>
      lastPage.length < pageSize ? undefined : allPages.reduce((n, p) => n + p.length, 0),
  };
}

function adsCountQueryOptions(input: {
  cats: string[];
  q?: string;
  saleKind?: SaleKindId;
  sellerType?: SellerTypeId;
  country?: "SE" | "ES";
}) {
  const saleKind = input.saleKind && input.saleKind !== "alla" ? input.saleKind : undefined;
  const sellerType =
    input.sellerType && input.sellerType !== "alla" ? input.sellerType : undefined;
  const country = input.country ?? "SE";
  return {
    queryKey: [
      "ads-count",
      input.cats.join(",") || null,
      input.q ?? null,
      saleKind ?? null,
      sellerType ?? null,
      country,
    ] as const,
    queryFn: () =>
      countAds({
        data: { categories: input.cats, search: input.q, saleKind, sellerType, country },
      }),
  };
}

export const Route = createFileRoute("/annonser")({
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    kategori: typeof search.kategori === "string" ? search.kategori : undefined,
    q: typeof search.q === "string" ? search.q : undefined,
    maxPris: num(search.maxPris),
    minPris: num(search.minPris),
    sort: SORTS.some((s) => s.id === search.sort) ? (search.sort as SortId) : undefined,
    salj: SALE_KINDS.some((s) => s.id === search.salj) ? (search.salj as SaleKindId) : undefined,
    saljare: SELLER_TYPES.some((s) => s.id === search.saljare)
      ? (search.saljare as SellerTypeId)
      : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Köp begagnat – alla annonser i hela Sverige | Sellavo" },
      {
        name: "description",
        content:
          "Bläddra bland begagnade prylar till salu i hela Sverige. Filtrera på kategori, pris och ort – hitta fynd nära dig på Sellavo.",
      },
      { property: "og:title", content: "Köp begagnat – alla annonser i hela Sverige | Sellavo" },
      {
        property: "og:description",
        content:
          "Begagnade prylar till salu i hela Sverige. Filtrera på kategori, pris och ort och hitta fynd nära dig.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://sellavo.com/annonser" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://sellavo.com/annonser" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Alla annonser på Sellavo",
          description:
            "Begagnade prylar till salu i hela Sverige, filtrerbart på kategori, pris och ort.",
          url: "/annonser",
          isPartOf: { "@type": "WebSite", name: "Sellavo" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Start", item: "/" },
            { "@type": "ListItem", position: 2, name: "Alla annonser", item: "/annonser" },
          ],
        }),
      },
    ],
  }),


  loaderDeps: ({ search }: { search: SearchParams }) => ({
    kategori: search.kategori,
    q: search.q,
    sort: search.sort,
    salj: search.salj,
    saljare: search.saljare,
  }),

  // Hämta första sidan redan på servern: listan syns direkt (bra för Google)
  // och webbläsaren visar samma innehåll som servern skickade.
  loader: async ({ context, deps }) => {
    // I webbläsaren sköter React Query hämtningen. Om loadern körde om vid varje
    // tangenttryck skulle sidan gå in i väntläge, tappa fokus i sökrutan och hoppa.
    if (typeof window !== "undefined") return;
    const cats = deps.kategori ? deps.kategori.split(",").filter(Boolean) : [];
    const sort: SortId = deps.sort ?? "senaste";
    const saleKind: SaleKindId = deps.salj ?? "alla";
    const sellerType: SellerTypeId = deps.saljare ?? "alla";
    await Promise.all([
      context.queryClient.ensureInfiniteQueryData(
        adsQueryOptions({ cats, q: deps.q, sort, saleKind, sellerType }) as never,
      ),
      context.queryClient.ensureQueryData(
        adsCountQueryOptions({ cats, q: deps.q, saleKind, sellerType }),
      ),
    ]).catch(() => undefined);
  },

  errorComponent: () => (
    <main className="mx-auto w-full max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
      <h1 className="text-2xl font-semibold">Vi kunde inte hämta annonserna</h1>
      <p className="mt-2 text-muted-foreground">Prova att ladda om sidan om en liten stund.</p>
    </main>
  ),

  notFoundComponent: () => (
    <main className="mx-auto w-full max-w-4xl px-4 py-16 text-center sm:px-6 lg:px-8">
      <h1 className="text-2xl font-semibold">Sidan finns inte</h1>
    </main>
  ),

  component: AdsPage,
});

export function AdsPage() {
  const { lang, country: market } = useLocale();
  const tx = lang === "en" ? {
    title: "All items for sale", intro: "Everything currently for sale on Sellavo. Choose a category or search freely by title.", list: "List", map: "Map", placeholder: "Search, e.g. bicycle, lamp, sofa…", search: "Search", clearSearch: "Clear search", allPrices: "All prices", allSellers: "All sellers", private: "Private", business: "Business", all: "All", fixed: "Fixed price", auction: "Auction", clear: "Clear all filters", loadingMap: "Loading map…", noMap: "No listings with a location matched your search.", loading: "Loading listings…", listing: "listing", listings: "listings", update: "Updating…", empty: "No listings matched your search. Try another category.", more: "Show more listings", fetching: "Loading more…"
  } : lang === "es" ? {
    title: "Todos los artículos en venta", intro: "Todo lo que está a la venta ahora en Sellavo. Elige una categoría o busca libremente por título.", list: "Lista", map: "Mapa", placeholder: "Busca, p. ej. bicicleta, lámpara, sofá…", search: "Buscar", clearSearch: "Borrar búsqueda", allPrices: "Todos los precios", allSellers: "Todos los vendedores", private: "Particular", business: "Empresa", all: "Todos", fixed: "Precio fijo", auction: "Subasta", clear: "Borrar todos los filtros", loadingMap: "Cargando mapa…", noMap: "Ningún anuncio con ubicación coincide con tu búsqueda.", loading: "Cargando anuncios…", listing: "anuncio", listings: "anuncios", update: "Actualizando…", empty: "Ningún anuncio coincide con tu búsqueda. Prueba otra categoría.", more: "Ver más anuncios", fetching: "Cargando más…"
  } : null;
  const sorts = tx ? [
    { id: "senaste" as const, label: lang === "en" ? "Newest" : "Más recientes" },
    { id: "nara" as const, label: lang === "en" ? "Near me" : "Cerca de mí" },
    { id: "billigast" as const, label: lang === "en" ? "Lowest price" : "Precio más bajo" },
    { id: "dyrast" as const, label: lang === "en" ? "Highest price" : "Precio más alto" },
    { id: "slutar-snart" as const, label: lang === "en" ? "Auctions ending soon" : "Subastas que terminan pronto" },
  ] : SORTS;
  const saleKinds = tx ? [{ id: "alla" as const, label: tx.all }, { id: "fixed" as const, label: tx.fixed }, { id: "auction" as const, label: tx.auction }] : SALE_KINDS;
  const sellerTypes = tx ? [{ id: "alla" as const, label: tx.allSellers }, { id: "private" as const, label: tx.private }, { id: "business" as const, label: tx.business }] : SELLER_TYPES;
  const { kategori, q, maxPris, minPris, sort, salj, saljare } = useSearch({ strict: false }) as SearchParams;
  const cats = useMemo(() => (kategori ? kategori.split(",").filter(Boolean) : []), [kategori]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [term, setTerm] = useState(q ?? "");
  const [view, setView] = useState<"lista" | "karta">("lista");
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const auctionsOn = useAuctionsEnabled();
  const activeSort: SortId = sort ?? "senaste";
  const activeSaleKind: SaleKindId = salj ?? "alla";
  const activeSellerType: SellerTypeId = saljare ?? "alla";

  // Kom ihåg senast valda sortering mellan besök.
  useEffect(() => {
    if (sort) {
      try {
        localStorage.setItem("sellavo:sortering", sort);
      } catch {
        /* ignorera full lagring */
      }
      return;
    }
    let saved: string | null = null;
    try {
      saved = localStorage.getItem("sellavo:sortering");
    } catch {
      saved = null;
    }
    if (saved && saved !== "senaste" && SORTS.some((s) => s.id === saved)) {
      navigate({ to: ".", search: { kategori, q, maxPris, minPris, salj, saljare, sort: saved as SortId }, replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  // Sidvis inläsning – inget tak på 1 000 annonser, man laddar fler så länge det finns.
  const { data, isLoading, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      ...adsQueryOptions({
      cats,
      q,
      sort: activeSort,
      saleKind: activeSaleKind,
      sellerType: activeSellerType,
      country: market,
    }),
      // Behåll föregående resultat medan nya hämtas så att UI:t inte blinkar tomt.
      placeholderData: keepPreviousData,
    });

  const { data: totalCount } = useQuery({
    ...adsCountQueryOptions({
      cats,
      q,
      saleKind: activeSaleKind,
      sellerType: activeSellerType,
      country: market,
    }),
    placeholderData: keepPreviousData,
  });

  const { data: places } = useQuery({ ...savedPlacesQuery(), enabled: !!user });

  // Markera mina egna annonser i listan.
  const fetchMyAds = useServerFn(getMyAds);
  const { data: myAds } = useQuery({
    queryKey: ["my-ads-slugs"],
    queryFn: () => fetchMyAds({}),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
  const ownSlugs = useMemo(
    () => new Set(((myAds ?? []) as { slug: string }[]).map((a) => a.slug)),
    [myAds],
  );

  const home = useMemo(() => {
    const hasPosition = (place: any) => place.latitude != null && place.longitude != null;
    const p =
      (places ?? []).find((x: any) => x.is_default && hasPosition(x)) ??
      (places ?? []).find(hasPosition);
    return p
      ? {
          latitude: p.latitude as number,
          longitude: p.longitude as number,
          label: p.label as string,
          address: p.address as string,
          location: p.location as string,
          isDefault: p.is_default as boolean,
        }
      : null;
  }, [places]);

  const [aiNote, setAiNote] = useState<string | null>(null);

  // Håll sökrutan i synk om q ändras utifrån (t.ex. via AI-tolkning eller bakåtknappen).
  useEffect(() => {
    setTerm(q ?? "");
  }, [q]);

  // Sök direkt medan man skriver (kort fördröjning) – ingen knapptryckning krävs.
  useEffect(() => {
    const next = term.trim();
    if (next === (q ?? "")) return;
    const id = setTimeout(() => {
      navigate({
        to: ".",
        search: { kategori, q: next || undefined, maxPris, minPris, sort, salj, saljare },
        replace: true,
        resetScroll: false,
      });
    }, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  const ads = useMemo(() => {
    const all = ((data?.pages ?? []).flat() ?? []) as (AdCardData & {
      latitude?: number | null;
      longitude?: number | null;
    })[];
    const filtered = all.filter(
      (a) =>
        (maxPris === undefined || a.price <= maxPris) &&
        (minPris === undefined || a.price >= minPris),
    );
    // Samma (förskjutna) position som kartan visar används för avståndet,
    // så att listan och markören alltid stämmer överens.
    const withDistance = filtered.map((a) => {
      const point = adDisplayCoords(a);
      return { ...a, distance: home && point ? distanceKm(home, point) : null };
    });
    if (activeSort !== "nara" || !home) return withDistance;
    return withDistance.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity));
  }, [data, maxPris, minPris, activeSort, home]);

  const topBids = useTopBids(ads);

  const applyAi = (f: AiFilters, explanation: string) => {
    setTerm(f.q ?? "");
    setAiNote(explanation);
    navigate({
      to: ".",
      search: {
        q: f.q,
        kategori: f.kategori,
        maxPris: f.maxPris,
        minPris: f.minPris,
        sort,
        salj,
        saljare,
      },
    });
  };

  const { data: mapData, isLoading: mapLoading } = useQuery({
    queryKey: ["ads-map", cats.join(",") || null, q ?? null, market],
    queryFn: () => listAdsForMap({ data: { categories: cats, search: q, country: market } }),
    enabled: view === "karta",
    staleTime: 60_000,
  });
  const mapAds = useMemo(() => (mapData ?? []) as MapAd[], [mapData]);

  return (
    <main className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 pb-16 pt-6 sm:pb-20 sm:pt-10">
      {/* Toppen: rubrik till vänster, vy-växlaren till höger. */}
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            {tx?.title ?? "Alla prylar till salu"}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground sm:text-base">
            {tx?.intro ?? "Allt som säljs just nu på Sellavo. Välj en kategori eller sök fritt bland titlarna."}
          </p>
        </div>
        <div className="inline-flex shrink-0 rounded-lg border border-border bg-card p-1">
          <ViewTab
            active={view === "lista"}
            onClick={() => setView("lista")}
            icon={LayoutGrid}
            label={tx?.list ?? "Lista"}
          />
          <ViewTab
            active={view === "karta"}
            onClick={() => setView("karta")}
            icon={MapIcon}
            label={tx?.map ?? "Karta"}
          />
        </div>
      </div>

      {/* Ett samlat filterkort: sök överst, filter i en lugn rad under. */}
      <div className="mt-5 rounded-xl border border-border bg-card p-3 shadow-sm sm:mt-7 sm:p-4">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAiNote(null);
             navigate({
               to: ".",
               search: { kategori, q: term.trim() || undefined, maxPris, minPris, sort, salj, saljare },
              resetScroll: false,
            });
          }}
        >
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder={tx?.placeholder ?? "Sök fritt, t.ex. cykel, bordslampa, soffa …"}
              className="bg-background pl-9 pr-9"
              aria-label={tx?.search ?? "Sök annonser"}
            />
            {term && (
              <button
                type="button"
                aria-label={tx?.clearSearch ?? "Rensa sökning"}
                onClick={() => setTerm("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground transition-colors hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Button type="submit" className="shrink-0">
            <Search className="h-4 w-4" />
            <span className="hidden sm:inline">{tx?.search ?? "Sök"}</span>
          </Button>
        </form>
        <AiSearchPanel term={term} onApply={applyAi} />

        <div className="mt-3 h-px bg-border" />

        {/* Rad 1: kategori, pris, sortering. Rad 2: de två segmenten. */}
        <div className="mt-3 grid gap-2 sm:grid-cols-3 sm:gap-3">
          <CategoryPicker
            value={cats}
            onChange={(slugs) =>
               navigate({
                 to: ".",
                 search: {
                   q, maxPris, minPris, sort, salj, saljare,
                  kategori: slugs.length ? slugs.join(",") : undefined,
                 },
              })
            }
          />

          <Select
            value={priceId(minPris, maxPris)}
            onValueChange={(v) => {
              const range = PRICES.find((p) => p.id === v);
               navigate({
                 to: ".",
                 search: {
                   kategori, q, sort, salj, saljare,
                  minPris: range?.min,
                  maxPris: range?.max,
                 },
              });
            }}
          >
            <SelectTrigger className="w-full" aria-label="Filtrera på pris">
              <SelectValue placeholder={tx?.allPrices ?? "Alla priser"} />
            </SelectTrigger>
            <SelectContent>
              {PRICES.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.label}
                </SelectItem>
              ))}
              {priceId(minPris, maxPris) === "eget" ? (
                <SelectItem value="eget">
                  {minPris ? `${minPris.toLocaleString("sv-SE")} kr` : "0 kr"} –{" "}
                  {maxPris ? `${maxPris.toLocaleString("sv-SE")} kr` : "uppåt"}
                </SelectItem>
              ) : null}
            </SelectContent>
          </Select>

          <Select
            value={activeSort}
            onValueChange={(v) =>
              navigate({ to: ".", search: { kategori, q, maxPris, minPris, salj, saljare, sort: v as SortId } })
            }
          >
            <SelectTrigger className="w-full" aria-label="Sortera annonser">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {sorts.filter((s) => auctionsOn || s.id !== "slutar-snart").map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="mt-2 grid gap-2 sm:mt-3 sm:grid-cols-2 sm:gap-3">
          {auctionsOn && (
          <div
            className="inline-flex w-full rounded-lg border border-border bg-background p-0.5"
            role="group"
            aria-label="Fast pris eller auktion"
          >
            {saleKinds.map((k) => (
              <button
                key={k.id}
                type="button"
                onClick={() =>
                  navigate({
                    to: ".",
                    search: {
                      kategori, q, maxPris, minPris, saljare,
                      salj: k.id === "alla" ? undefined : k.id,
                      // "Slutar snart" är bara relevant för auktioner.
                      sort:
                        k.id === "auction" ? sort : sort === "slutar-snart" ? undefined : sort,
                    },
                  })
                }
                className={`h-9 flex-1 whitespace-nowrap rounded-md px-3 text-sm font-medium transition-colors ${
                  activeSaleKind === k.id
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {k.label}
              </button>
            ))}
          </div>
          )}

          <div
            className="inline-flex w-full rounded-lg border border-border bg-background p-0.5"
            role="group"
            aria-label="Privat eller företag"
          >
            {sellerTypes.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() =>
                  navigate({
                    to: ".",
                    search: {
                      kategori, q, maxPris, minPris, sort, salj,
                      saljare: t.id === "alla" ? undefined : t.id,
                    },
                  })
                }
                className={`h-9 flex-1 whitespace-nowrap rounded-md px-3 text-sm font-medium transition-colors ${
                  activeSellerType === t.id
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Aktiva filter och avståndsinfo som lugna rader längst ned i kortet. */}
        {(aiNote || minPris !== undefined || maxPris !== undefined || q || cats.length > 0) && (
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            {aiNote && (
              <span className="rounded-md bg-ocean-100 px-2 py-1 text-foreground/80">{aiNote}</span>
            )}
            {minPris !== undefined && (
              <span className="rounded-md border border-border px-2 py-1 text-muted-foreground">
                Från {minPris.toLocaleString("sv-SE")} kr
              </span>
            )}
            {maxPris !== undefined && (
              <span className="rounded-md border border-border px-2 py-1 text-muted-foreground">
                Upp till {maxPris.toLocaleString("sv-SE")} kr
              </span>
            )}
            <button
              type="button"
              className="rounded-md px-2 py-1 font-medium text-primary underline underline-offset-2"
              onClick={() => {
                setAiNote(null);
                setTerm("");
                navigate({ to: ".", search: {} as never });
              }}
            >
              {tx?.clear ?? "Rensa alla filter"}
            </button>
          </div>
        )}

        {activeSort === "nara" &&
          (home ? (
            <div className="mt-3 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              <Home className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
              <span className="min-w-0">
                Sorterar från <span className="font-semibold text-foreground">{home.label}</span>
                {home.address || home.location ? (
                  <span className="text-muted-foreground">
                    {" "}
                    · {[home.address, home.location].filter(Boolean).join(", ")}
                  </span>
                ) : null}
              </span>
              <Link
                to="/adresser"
                aria-label={`Byt hemadress, nuvarande: ${home.label}`}
                className="inline-flex min-h-9 shrink-0 items-center rounded-md border border-border bg-card px-3 text-xs font-semibold text-primary transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                Byt
              </Link>
            </div>
          ) : (
            <p className="mt-3 flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
              <Navigation className="h-3.5 w-3.5 shrink-0" />
              {user ? (
                <>
                  Ingen hemadress med position är vald.{" "}
                  <Link
                    to="/adresser"
                    className="font-semibold text-primary underline underline-offset-2"
                  >
                    Välj hemadress
                  </Link>
                </>
              ) : (
                "Logga in och spara en hemadress för att sortera efter avstånd."
              )}
            </p>
          ))}
      </div>


      {view === "karta" ? (
        <div className="mt-6">
          {mapLoading || !mounted ? (
            <div className="flex h-[70vh] min-h-[420px] w-full items-center justify-center rounded-lg border border-border bg-muted text-muted-foreground">
              {tx?.loadingMap ?? "Laddar karta…"}
            </div>
          ) : mapAds.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground">
              {tx?.noMap ?? "Inga annonser med platsinformation matchade din sökning."}
            </p>
          ) : (
            <>
              <Suspense
                fallback={
                  <div className="flex h-[70vh] min-h-[420px] w-full items-center justify-center rounded-lg border border-border bg-muted text-muted-foreground">
                    {tx?.loadingMap ?? "Laddar karta…"}
                  </div>
                }
              >
                <AdsClusterMap ads={mapAds} />
              </Suspense>
              <p className="mt-2 text-xs text-muted-foreground">
                {mapAds.length.toLocaleString("sv-SE")} annonser på kartan. Klicka på ett kluster
                för att zooma in, och på en nål för att se annonsen.
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="mt-6 sm:mt-8">
          <div
            aria-live="polite"
            className="mb-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground sm:text-sm"
          >
            {isLoading ? (
              <span>{tx?.loading ?? "Laddar annonser…"}</span>
            ) : (
              <>
                <span>
                  <span className="font-semibold text-foreground">
                    {ads.length.toLocaleString("sv-SE")}
                  </span>
                  {typeof totalCount === "number" && totalCount > ads.length
                    ? ` av ${totalCount.toLocaleString("sv-SE")}`
                    : ""}{" "}
                   {ads.length === 1 ? (tx?.listing ?? "annons") : (tx?.listings ?? "annonser")} ·{" "}
                   {sorts.find((s) => s.id === activeSort)?.label.toLowerCase()}
                  {activeSort === "nara" && home ? ` från ${home.label}` : ""}
                </span>
                {cats.length > 0 && (
                  <span>
                    · {cats.length} kategori{cats.length === 1 ? "" : "er"}
                  </span>
                )}
                {q && <span>· sökning “{q}”</span>}
                <SaveSearchButton
                  query={{
                    q: q ?? null,
                    categories: cats.length ? cats : null,
                    minPrice: minPris ?? null,
                    maxPrice: maxPris ?? null,
                    saleKind: activeSaleKind === "alla" ? null : (activeSaleKind as "fixed" | "auction"),
                    sellerType:
                      activeSellerType === "alla" ? null : (activeSellerType as "private" | "business"),
                  }}
                />
              </>
            )}
            {isFetching && !isLoading && !isFetchingNextPage && (
              <span className="inline-flex items-center gap-1 text-primary">
                 <Loader2 className="h-3.5 w-3.5 animate-spin" /> {tx?.update ?? "Uppdaterar…"}
              </span>
            )}
          </div>
          {isLoading ? null : ads.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground">
              {tx?.empty ?? "Inga annonser matchade din sökning. Prova en annan kategori."}
            </p>
          ) : (
            <>
              <div
                className={`grid gap-4 transition-opacity sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 ${
                  isFetching && !isFetchingNextPage ? "opacity-60" : "opacity-100"
                }`}
              >
                {ads.map((ad, i) => (
                  <AdCard
                    key={ad.slug}
                    ad={ad}
                    priority={i === 0}
                    isOwn={ownSlugs.has(ad.slug)}
                    topBid={topBids.get(ad.slug) ?? null}
                    distanceLabel={
                      activeSort === "nara" && ad.distance != null
                        ? `Ca ${kmLabel(ad.distance)}`
                        : null
                    }
                  />
                ))}
              </div>
              {hasNextPage && (
                <div className="mt-6 flex justify-center">
                  <Button
                    variant="outline"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    className="min-w-48"
                  >
                    {isFetchingNextPage ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" /> {tx?.fetching ?? "Hämtar fler…"}
                      </>
                    ) : (
                      (tx?.more ?? "Visa fler annonser")
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </main>
  );
}


function ViewTab({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: LucideIcon;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-sm px-3 py-1.5 text-sm font-medium transition-colors ${
        active ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:text-foreground"
      }`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}
